import { FC, Fragment } from "react"
import { PageId } from "../../common/pageId"
import { routeMap } from "../../components/generic/navbar/routeMap"

import { PageTemplate } from "../../components/pageTemplate/PageTemplate"
import { SidebarPageContent } from "../../components/SidebarPageContent"
import { Link } from "react-router-dom"
import PrivacyPolicy from "../../documents/OCS_Privacy_Policy.pdf"
import TermsAndConditions from "../../documents/OCS_Terms_and_Conditions.pdf"
import ApplicationForm from "../../documents/OCS_Application_Form.pdf"

export const HowToApply: FC = () => {
  return (
    <PageTemplate
      pageTitle="How To Apply"
      pageContentClassName="info-page-container"
    >
      <SidebarPageContent
        route={routeMap[PageId.HowToApply]}
        sidebarItems={[
          {
            id: "WhatIsOCS",
            header: "What is OCS?",
            body: (
              <Fragment>
                <p>
                  The Online Coordination System (OCS) has been designed to
                  provide airline and general/business aviation customers with
                  interactive facilities to access schedule data from
                  participating coordinators, who are responsible for providing
                  schedule data collection, facilitation and slot allocation
                  services at airports.
                </p>
                <p>
                  OCS allows customers to access the databases of all
                  participating coordinators following one easy verification
                  process. Customers can directly manage their own slot
                  portfolios (add, change and delete flights), produce reports
                  on their own slot portfolios and view and download schedule
                  data of all carriers serving the airport.
                </p>
                <p>
                  To access and use the OCS, you will need to enter into an
                  agreement and set up one or more authorised user accounts with
                  the OCS Portal Administrator. You may apply to enter into this
                  agreement by completing and submitting the Account Application
                  Form available from this Web Portal. The annual access charges
                  must also be paid in advance.
                </p>
                <p>
                  The full service will not be available until you have
                  completed the application process.
                </p>
                <p>
                  The agreement will be subject to the OCS Terms and Conditions
                  Of Use and Privacy Policy, which you should read carefully
                  before submitting your application.
                </p>
              </Fragment>
            )
          },
          {
            id: "systemDescription",
            header: "System description",
            body: (
              <Fragment>
                <p>
                  OCS can be configured to allow each user access to particular
                  functionality, for example, read only access, edit access for
                  single dates only, or full access. The configuration is
                  applicable by airport and by season.
                </p>
                <p>
                  OCS enables authorised customers to access the following
                  services, based on user permissions:
                </p>
                <p className="bold m-0">View Flights</p>
                <p>
                  The ability to view flights based on selection criteria for
                  particular airports and seasons.
                </p>
                <p className="bold m-0">Edit Flights</p>
                <p>
                  The ability for customers to edit their own flight schedules
                  or, where appropriately authorised, other customers' flight
                  schedules, including deletions and offer searches. Ad hoc
                  (single date) and series modes are provided.
                </p>
                <p>
                  In addition users can add, edit or delete a flight
                  simultaneously at both the departure and arrival airports
                  provided both coordinators databases are accessible via OCS.
                </p>
                <p className="bold m-0">Add Flights</p>
                <p>
                  An online slot request form for new ad hoc (single date) and
                  series flights with an automated offer response.
                </p>
                <p className="bold m-0">Delete Flights</p>
                <p>
                  The ability for customers to delete ad hoc or series flights
                  in their own portfolio.
                </p>
                <p className="bold m-0">Edit Offers</p>
                <p>
                  The ability for the customer to accept, pend or decline offers
                  made from the coordinator’s principal coordination system.
                </p>
                <p className="bold m-0">Download Schedules</p>
                <p>
                  The ability to download schedule data in predefined formats
                  based on selection criteria for particular airports and
                  seasons. This can be done either via the View or Edit flights
                  pages, or directly for large amounts of data.
                </p>
                <p className="bold m-0">Slot Availability</p>
                <p>
                  Graphical displays in a variety of predefined formats showing
                  runway and passenger slot availability at each airport and
                  season. This is available to all interested parties, not just
                  registered users of OCS.
                </p>
                <p className="bold m-0">Slot Monitoring</p>
                <p>
                  The ability to download a file containing the current slot
                  performance against the use it or lose it rules of the
                  customers own flights at specified airports.
                </p>
                <p className="bold m-0">Reporting</p>
                <p>
                  OCS enables users to interrogate the coordinators database
                  with a series of query statements to obtain simple or complex
                  reports on schedule data held in the database.
                </p>
                <p>
                  <span className="bold">Note:</span> Not all the services in
                  OCS are available for all the airports that can be accessed
                  via OCS.
                </p>
              </Fragment>
            )
          },
          {
            id: "charges",
            header: "Charges",
            body: (
              <Fragment>
                <p>
                  The charge to access OCS is €450 per annum for 2 user IDs for
                  access to the database of all participating coordinators,
                  initially payable 12 months after the acceptance of the
                  application. No deductions will be made for access to the OCS
                  for less than a 12 month period. For new airline applications
                  the first 12 months of OCS usage is free.
                </p>
                <p>
                  For administrative and/or operational reasons some customers
                  may want multiple user accounts and access to the OCS.
                </p>
                <p>
                  The charge for multiple user accounts to access the OCS is the
                  charge for 2 user IDs set out above, plus €60 for each
                  additional user account required for 12 months from acceptance
                  of each user account application, payable in Euros on
                  submission of the application for each additional licensed
                  user. No deductions will be made for the access to the OCS for
                  less than a 12 month period.
                </p>
                <p>
                  All charges are exclusive of Value Added Tax, where
                  applicable, and any other relevant taxes which may be payable
                  or applicable from time to time. All charges must be paid by
                  credit card.
                </p>
                <p>
                  If the agreement is renewed, the charges for the period
                  covered by the renewal will be payable on renewal.
                </p>
              </Fragment>
            )
          },
          {
            id: "security",
            header: "Security",
            body: (
              <Fragment>
                <p className="m-0 bold">Customer Authentication</p>
                <p>
                  It is extremely important that access to the OCS is restricted
                  to authorised customers and users and that a user, when
                  logging on to the OCS Portal, really is who they claim to be.
                </p>
                <p>
                  OCS customers will, within a reasonable time following the OCS
                  Portal Administrator’s acceptance of the customer's
                  application to use the OCS, be given a user ID, a password and
                  a pass code for each of the authorised users indicated on
                  their application. The user IDs, passwords and pass codes
                  cannot be changed by the customer. The user ID, passwords and
                  pass codes will be changed periodically by the OCS
                  administration team and the customer will be notified. The
                  user IDs, passwords and pass codes will be sent by email to
                  the customer's email address specified on their application
                  form. Only when the customer has entered this information
                  correctly during login, will access be granted.
                </p>
                <p>
                  The OCS Portal Administrator will also, within a reasonable
                  time following its acceptance of the customer's application to
                  use the OCS, implement the necessary authorisation with the
                  OCS to enable access by the customer.
                </p>
                <p className="m-0 bold">Firewalls</p>
                <p>
                  The connection between the customer and the OCS Portal and the
                  link from the OCS Portal to the database of each participating
                  coordinator is protected by SSL (Secure Socket Layers) which
                  encrypts the data with the intention of ensuring privacy.
                </p>
                <p>
                  The OCS is a web application accessed from a web browser on
                  the PCs of the customer's authorised users. Customers who have
                  built a firewall system between their Local Area Network (LAN)
                  and the Internet, should be able to let the OCS messages pass
                  without problems since these communications are based on the
                  standard web protocols HTTP (Hypertext Transfer Protocol) and
                  HTTPS (Secure HTTP) supported, as far as we are aware, by
                  practically all commercial firewall products.
                </p>
                <p>
                  The transfer of some restricted pages of the OCS is encrypted
                  by use of the HTTPS protocol. Customers do not need to have a
                  X.509 client certificate installed in their web browser in
                  order to access these restricted pages. When in doubt,
                  customers can consult with their firewall system vendor for
                  configuring the firewall filters or proxy servers for HTTP and
                  HTTPS. It is the customer's responsibility to ensure that its
                  firewalls allow the use of the OCS.
                </p>
              </Fragment>
            )
          },
          {
            id: "technicalRequirements",
            header: "Technical requirements",
            body: (
              <Fragment>
                <p>
                  OCS supports most major browsers and devices. However, in
                  order to ensure full functionality, below is a list of
                  browsers which are fully supported
                </p>
                <p className="m-0 bold">Browsers</p>
                <p> One of the following:</p>
                <p>
                  <ul>
                    <li>Google Chrome (latest stable version)</li>
                    <li>Firefox (latest stable version)</li>
                    <li>Microsoft Edge (latest stable version)</li>
                    <li>Safari on MacOS (latest stable version)</li>
                  </ul>
                </p>
                <p>
                  If you are using a browser or browser version that is not
                  listed, OCS might work anyway but we cannot guarantee full
                  functionality. Hence, if you are experiencing an issue, we
                  recommend that you update to a supported browser version.
                </p>
                <p>
                  A stable release is an official release that has been tested
                  as thoroughly as possible and is as reliable as possible. This
                  is necessary to keep up with security, compatibility and
                  functionality. A good resource for finding the lastest stable
                  version is Wikipedia, that also lists version history.
                </p>
                <p className="m-0 bold">Communications / Performance</p>
                <p>The OCS Portal is hosted by PDC in Copenhagen.</p>
                <p>
                  The overall performance of the OCS, observed by the customer,
                  is dependent upon many aspects of the technology in use e.g.
                  line speed, the quality of the local internet service
                  provider, the network load, the remote web or network
                  server(s), the computer's performance (load, stand-alone,
                  networked) etc.
                </p>
                <p>
                  Some of these aspects are variable (e.g. loads) and some are
                  outside OCS Coordinators’ control. Overall performance of the
                  OCS is therefore almost impossible to quantify.
                </p>
              </Fragment>
            )
          },
          {
            id: "support",
            header: "Customer support & queries",
            body: (
              <Fragment>
                <p className="m-0 bold">
                  Support from the OCS Portal Administrator
                </p>
                <p>
                  In addition to the online Help accessed from the OCS, the OCS
                  Portal Administrator offers access to a Customer Service
                  Centre (CSC) to support all its customers and deal with any
                  queries that they may have. The CSC team is also responsible
                  for assisting the customers in setting up and configuring
                  their access to the OCS. Queries concerning any aspect of the
                  OCS, access or problems encountered during configuration
                  should be directed to the CSC. The CSC team is only available
                  during office hours (0800 - 1800 UK local Monday to Friday)
                  and may be contacted by telephoning +44 (0) 20 8564 0600.
                </p>
                <p>
                  The customer will need to successfully complete a security
                  check using the question and answer pairs set out in the
                  customer's application when contacting the CSC to identify
                  that they are an authorised OCS customer.
                </p>
                <p>
                  When using the OCS simply click on the Help button to provide
                  the contact information for the CSC.
                </p>
                <p className="m-0 bold">
                  Support from the Internet Service Provider
                </p>
                <p>
                  In case of communications problems the customer should, in the
                  first instance, consult the maintenance services of their
                  Internet Service Provider (ISP). The CSC will be happy to
                  discuss this problem but will be unable to help resolve
                  customer or ISP based communication problems.
                </p>
                <p className="m-0 bold">Training</p>
                <p>
                  The user interface to OCS has been designed to be as intuitive
                  as possible for those with scheduling experience. Customers
                  will have access to online Help when accessing the OCS and the
                  CSC which should be able to deal with most customer queries.
                </p>
              </Fragment>
            )
          },
          {
            id: "apply",
            header: "Apply now",
            body: (
              <Fragment>
                <p>
                  Please download the Application Form and complete in full.
                </p>
                <ul>
                  <li>
                    <a
                      href={TermsAndConditions}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Terms & Conditions
                    </a>
                  </li>
                  <li>
                    <a href={ApplicationForm} target="_blank" rel="noreferrer">
                      Application Form
                    </a>
                  </li>
                  <li>
                    <a href={PrivacyPolicy} target="_blank" rel="noreferrer">
                      Privacy Policy
                    </a>
                  </li>
                </ul>
                <p>
                  You will need Adobe Acrobat to view these documents. Visit the{" "}
                  <a
                    href="https://www.adobe.com/acrobat.html"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Adobe Acrobat
                  </a>{" "}
                  site for more information.
                </p>

                <p>
                  Payment may be made by one of the options specified on the
                  Application Form. Please send the completed original
                  application form and any payment to:
                </p>
                <p className="m-0 bold">OCS Portal Administrator</p>
                <p className="m-0 bold">c/o Airport Coordination Limited</p>
                <p className="m-0 bold">Online Coordination</p>
                <p className="m-0 bold">01 Meadlake Place</p>
                <p className="m-0 bold">Thorpe Lea Road</p>
                <p className="m-0 bold">Egham</p>
                <p className="m-0 bold">Surrey</p>
                <p className="m-0 bold">TW20 8HE</p>
                <p className="bold">United Kingdom</p>

                <p>
                  Alternatively please send completed form via email to
                  help@online-coordination.com in the form of a scanned PDF
                  file.
                </p>
                <p>
                  If you require further information about OCS or the
                  application process, please{" "}
                  <Link to={routeMap[PageId.ContactUs]}>contact us</Link>.
                </p>
              </Fragment>
            )
          }
        ]}
      />
    </PageTemplate>
  )
}
